import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "../../../../../../../../../../../hooks/use-debounce";
import useDisclosure from "../../../../../../../../../../../hooks/use-disclosure";
import { useGetAllScreeningPreview } from "../../../../../../../../../../../queries/encounter/screening/use-get-all-screening-preview";
import { useGetAllScreenerType } from "../../../../../../../../../../../queries/patient-chart/assessment/assement-forms/use-get-all-screener-type";
import {
  DEF_PAGINATION,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../../../../../../../utils/utils";

/**
 * Screening Assessments state for the DAP note — the screener-type options, the
 * submitted screeners for this appointment and the drawer they are added from.
 *
 * The "Add Screening" button sits next to "Add ICD Codes" while the submitted
 * screeners are listed further down the Assessment section, so the state lives
 * here and the two halves are rendered by <DapScreeningButton /> and
 * <DapScreeningPreview />.
 */
export const useDapScreening = () => {
  const screeningDisclosure = useDisclosure();
  const appointmentid = useSearchParams()[0].get("appointmentid");

  const [search, setSearchStringKey, instantSearchStringKey] =
    useDebounce<string>("");

  const { data: screenerTypeList } = useGetAllScreenerType({
    pagination: {
      page: 0,
      pageSize: 5,
    },
    xTENANTID: getXtenantId(),
    searchString: search,
  });

  const screenerList = useMemo(() => {
    return screenerTypeList?.data?.content?.map((item) => {
      // The endpoint returns plain screener-type strings (e.g. "PHQ_9") even
      // though the query types its content as OptionData.
      const screenerType = item as unknown as string;
      return {
        value: screenerType,
        label: replaceUnderScoreAddSpace(screenerType),
      };
    });
  }, [screenerTypeList]);

  const { data: screeners } = useGetAllScreeningPreview({
    pagination: DEF_PAGINATION,
    gridSorting: [],
    appointmentUuid: appointmentid ?? "",
    xTENANTID: getXtenantId(),
  });

  return {
    screeningDisclosure,
    screenerList,
    screeners: screeners?.data,
    instantSearchStringKey,
    setSearchStringKey,
  };
};

export type DapScreening = ReturnType<typeof useDapScreening>;
